/**
 * ErrorBoundary — 未ハンドルのエラーが画面全体をクラッシュしないよう防護 (経験則 #50)
 * ルート単位で噛ませる。
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'
import Icon from './Icon'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-base px-6 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-review-bg text-review">
            <Icon name="alert-circle" size={32} aria-hidden={false} />
          </div>
          <h1 className="mb-2 text-xl font-bold text-text-primary">
            画面の表示に問題が発生しました
          </h1>
          <p className="mb-6 text-sm text-text-secondary">
            しばらく時間をおいてから再度お試しください
          </p>
          <button onClick={this.handleReset} className="btn-primary px-6">
            トップページへ戻る
          </button>
          {this.state.error && (
            <details className="mt-6 text-left text-xs text-text-muted">
              <summary className="cursor-pointer">エラー詳細</summary>
              <pre className="whitespace-pre-wrap break-all">{this.state.error.message}</pre>
            </details>
          )}
        </div>
      )
    }

    return this.props.children
  }
}
